import { reflectKeys } from "@utils/key.reflactor";

export const DRAWER_NAME = {
  ATTEMPT: "attempt",
  SETTINGS: "sub-content",
};

export const PAGE_DRAWER_TRANSITION_DURATION_MS = 300;

export const DRAWER_CONTENT_TYPE = {
  ATTEMPT: {
    ANNOTATION: "annotation",
    CONVERSATION: "conversation",
  },
};

export const SA_APP_FEATURE_TYPE = reflectKeys(["SA_ANSWER", "SA_RUBRIC"]);

export const ACP_HTML5_APP_FEATURE_TYPE = reflectKeys([
  "ACP_HTML5_ANTHROPIC",
  "ACP_HTML5_RE_ANTHROPIC",
]);

export const INTEGRATOR_APP_FEATURE_CATEGORY = reflectKeys([
  "ALS",
  "TTS",
  "SE",
  "GC",
  "SA",
  "AK",
  "GOOGLE_DRIVE",
  "GOOGLE_MEET",
  "ACP_MODULE",
  "ACP_ACTIVITY",
  "ACP_COMPONENT",
  "ACP_COMPONENT_RE",
  "ACP_RUBRIC",
  "ACP_HTML5",
  "ACP_IMAGE",
  "ACP_GAMIFICATION",
  "DA_POST",
  "DA_AGG_FRQ",
  "DA_QUIZ",
  "DA_AGG_QUIZ",
  "DA_ACTIVITY_CONVO",
  "HTML5",
  "LA_POST",
  "LA_TEAM",
  "LA_SPEECH",
  "LA_TEXT_QUERY",
  "AFA",
]);

export const INTEGRATOR_APP_PROVIDER = reflectKeys([
  "ANTHROPIC",
  "OPENAI",
  "AZURE",
  "LITERATU",
  "ALGEBRAKIT",
  "GOOGLE",
  "ADAPTEMY",
  "SLS",
]);

export const APP_LANGUAGE = reflectKeys(["EN", "ZH", "MS", "TA"]);

export const INTEGRATOR_APP_CATEGORY_MAP = {
  ACP: {
    category: [
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_RUBRIC,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE,
      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_GAMIFICATION,
    ],
    provider: [INTEGRATOR_APP_PROVIDER.ANTHROPIC, INTEGRATOR_APP_PROVIDER.OPENAI],
  },
  DA: {
    category: [
      INTEGRATOR_APP_FEATURE_CATEGORY.DA_POST,
      INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_FRQ,
      INTEGRATOR_APP_FEATURE_CATEGORY.DA_QUIZ,
      INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_QUIZ,
      INTEGRATOR_APP_FEATURE_CATEGORY.DA_ACTIVITY_CONVO,
    ],
    provider: [INTEGRATOR_APP_PROVIDER.ANTHROPIC],
  },
  LA: {
    category: [
      INTEGRATOR_APP_FEATURE_CATEGORY.LA_POST,
      INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEAM,
      INTEGRATOR_APP_FEATURE_CATEGORY.LA_SPEECH,
      INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEXT_QUERY,
    ],
    provider: [INTEGRATOR_APP_PROVIDER.OPENAI],
  },
  SA: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.SA],
    provider: [INTEGRATOR_APP_PROVIDER.OPENAI],
    featureType: [SA_APP_FEATURE_TYPE.SA_ANSWER, SA_APP_FEATURE_TYPE.SA_RUBRIC],
  },
  GC: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.GC],
    provider: [INTEGRATOR_APP_PROVIDER.LITERATU],
  },
  AK: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.AK],
    provider: [INTEGRATOR_APP_PROVIDER.ALGEBRAKIT],
  },
  ALS: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.ALS],
    provider: [INTEGRATOR_APP_PROVIDER.ADAPTEMY],
  },
  HTML5: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.HTML5],
    provider: [INTEGRATOR_APP_PROVIDER.SLS],
  },
  GOOGLE_DRIVE: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_DRIVE],
    provider: [INTEGRATOR_APP_PROVIDER.GOOGLE],
  },
  GOOGLE_MEET: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_MEET],
    provider: [INTEGRATOR_APP_PROVIDER.GOOGLE],
  },
  SE: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.SE],
    provider: [INTEGRATOR_APP_PROVIDER.AZURE],
    language: [APP_LANGUAGE.EN, APP_LANGUAGE.ZH, APP_LANGUAGE.MS, APP_LANGUAGE.TA],
  },
  TTS: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.TTS],
    provider: [INTEGRATOR_APP_PROVIDER.AZURE],
    language: [APP_LANGUAGE.EN, APP_LANGUAGE.ZH, APP_LANGUAGE.MS, APP_LANGUAGE.TA],
  },
  AFA: {
    category: [INTEGRATOR_APP_FEATURE_CATEGORY.AFA],
    provider: [INTEGRATOR_APP_PROVIDER.OPENAI],
  },
};

export const APP_USAGE_TYPE = reflectKeys(["FEEDBACK", "FEEDBACK_AND_MARKING"]);

export const LAST_ACCESSED_USERS_MAX_LENGTH = 2;

export const SHORT_ANSWER_VARIABLES = [
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_sa_subject_question",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_sa_level_question",
  },
  {
    variable: "app_configuration_variable_question",
    definition: "app_configuration_variable_definition_question",
  },
  {
    variable: "app_configuration_variable_student_response",
    definition: "app_configuration_variable_definition_student_response",
  },
  {
    variable: "app_configuration_variable_modal_answer",
    definition: "app_configuration_variable_definition_modal_answer",
  },
  {
    variable: "app_configuration_variable_marks",
    definition: "app_configuration_variable_definition_marks",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_sa_instructions",
  },
];

export const SHORT_ANSWER_RUBRIC_VARIABLES = [
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_sa_subject_question",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_sa_level_question",
  },
  {
    variable: "app_configuration_variable_question",
    definition: "app_configuration_variable_definition_question",
  },
  {
    variable: "app_configuration_variable_student_response",
    definition: "app_configuration_variable_definition_student_response",
  },
  {
    variable: "app_configuration_variable_marks",
    definition: "app_configuration_variable_definition_marks",
  },
  {
    variable: "app_configuration_variable_rubric_marking",
    definition: "app_configuration_variable_definition_rubric_marking",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_sa_rubric_instructions",
  },
];

export const AA_MODULE_VARIABLES = [
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level_section",
  },
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject_section",
  },
  {
    variable: "app_configuration_variable_lesson_tags",
    definition: "app_configuration_variable_definition_lesson_tags",
  },
  {
    variable: "app_configuration_variable_lesson_title",
    definition: "app_configuration_variable_definition_lesson_title",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_module_instructions",
  },
  {
    variable: "app_configuration_variable_no_of_sections",
    definition: "app_configuration_variable_definition_module_no_of_sections",
  },
  {
    variable: "app_configuration_variable_no_of_activities",
    definition: "app_configuration_variable_definition_module_no_of_activities",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_module_knowledge_base",
  },
  {
    variable: "app_configuration_variable_kat_tag",
    definition: "app_configuration_variable_definition_module_kat_tag",
  },
];

export const AA_ACTIVITY_VARIABLES = [
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level",
  },
  {
    variable: "app_configuration_variable_section_tags",
    definition: "app_configuration_variable_definition_section_tags",
  },
  {
    variable: "app_configuration_variable_section_title",
    definition: "app_configuration_variable_definition_section_title",
  },
  {
    variable: "app_configuration_variable_section_notes",
    definition: "app_configuration_variable_definition_section_notes",
  },
  {
    variable: "app_configuration_variable_additional_prompts",
    definition: "app_configuration_variable_definition_additional_prompts_activity",
  },
  {
    variable: "app_configuration_variable_duration",
    definition: "app_configuration_variable_definition_duration_activity",
  },
  {
    variable: "app_configuration_variable_no_of_activities",
    definition: "app_configuration_variable_definition_no_of_activities",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_knowledge_base",
  },
  {
    variable: "app_configuration_variable_templates_module_selection",
    definition: "app_configuration_variable_definition_templates_module_selection_activity",
  },
];

export const AA_COMPONENT_VARIABLES = [
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level",
  },
  {
    variable: "app_configuration_variable_section_tags",
    definition: "app_configuration_variable_definition_section_tags",
  },
  {
    variable: "app_configuration_variable_activity_title",
    definition: "app_configuration_variable_definition_activity_title",
  },
  {
    variable: "app_configuration_variable_activity_notes",
    definition: "app_configuration_variable_definition_activity_notes",
  },
  {
    variable: "app_configuration_variable_additional_prompts",
    definition: "app_configuration_variable_definition_additional_prompts_component",
  },
  {
    variable: "app_configuration_variable_duration",
    definition: "app_configuration_variable_definition_duration_component",
  },
  {
    variable: "app_configuration_variable_no_of_components",
    definition: "app_configuration_variable_definition_no_of_components",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_knowledge_base",
  },
  {
    variable: "app_configuration_variable_discussion_templates",
    definition: "app_configuration_variable_definition_discussion_templates",
  },
  {
    variable: "app_configuration_variable_templates_module_selection",
    definition: "app_configuration_variable_definition_templates_module_selection",
  },
];

export const AA_RE_COMPONENT_VARIABLES = [
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject_component",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level_component",
  },
  {
    variable: "app_configuration_variable_section_tags",
    definition: "app_configuration_variable_definition_section_tags",
  },
  {
    variable: "app_configuration_variable_original_component_type",
    definition: "app_configuration_variable_definition_original_component_type",
  },
  {
    variable: "app_configuration_variable_original_component",
    definition: "app_configuration_variable_definition_original_component",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_component_regen_knowledge_base",
  },
  {
    variable: "app_configuration_variable_additional_prompts",
    definition: "app_configuration_variable_definition_additional_prompts_activity",
  },
  {
    variable: "app_configuration_variable_no_of_components",
    definition: "app_configuration_variable_definition_no_of_components_regen",
  },
  {
    variable: "app_configuration_variable_component_types",
    definition: "app_configuration_variable_definition_component_types",
  },
  {
    variable: "app_configuration_variable_discussion_templates",
    definition: "app_configuration_variable_definition_discussion_templates",
  },
];

export const AA_RUBRIC_VARIABLES = [
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level_question",
  },
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject_question",
  },
  {
    variable: "app_configuration_variable_question_tags",
    definition: "app_configuration_variable_definition_question_tags",
  },
  {
    variable: "app_configuration_variable_question_body",
    definition: "app_configuration_variable_definition_question",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_rubric_instructions",
  },
  {
    variable: "app_configuration_variable_rubric",
    definition: "app_configuration_variable_definition_rubric",
  },
];

export const AA_HTML5_GENERATION_VARIABLES = [
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_acp_html5_instructions",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_acp_html5_knowledge_base",
  },
];

export const AA_HTML5_ITERATION_VARIABLES = [
  {
    variable: "app_configuration_variable_existing_html",
    definition: "app_configuration_variable_definition_existing_html",
  },
  {
    variable: "app_configuration_variable_existing_css",
    definition: "app_configuration_variable_definition_existing_css",
  },
  {
    variable: "app_configuration_variable_existing_javascript",
    definition: "app_configuration_variable_definition_existing_javascript",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_acp_html5_instructions",
  },
];

export const AA_IMAGE_VARIABLES = [
  {
    variable: "app_configuration_variable_context",
    definition: "app_configuration_variable_definition_acp_image_context",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_acp_image_instructions",
  },
];

export const AA_GAMIFICATION_VARIABLES = [
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level_section",
  },
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject_section",
  },
  {
    variable: "app_configuration_variable_section_and_activity_data",
    definition: "app_configuration_variable_definition_acp_gamification_section_and_activity_data",
  },
  {
    variable: "app_configuration_variable_game_stories",
    definition: "app_configuration_variable_definition_acp_gamification_game_stories",
  },
  {
    variable: "app_configuration_variable_achievements",
    definition: "app_configuration_variable_definition_acp_gamification_achievements",
  },
  {
    variable: "app_configuration_variable_game_avatars",
    definition: "app_configuration_variable_definition_acp_gamification_game_avatars",
  },
  {
    variable: "app_configuration_variable_digital_badges",
    definition: "app_configuration_variable_definition_acp_gamification_digital_badges",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_acp_gamification_instructions",
  },
];

export const DA_FRQ_VARIABLES = [
  {
    variable: "app_configuration_variable_frq_agg_responses",
    definition: "app_configuration_variable_definition_frq_agg_responses",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_instructions",
  },
];

export const DA_FORUM_VARIABLES = [
  {
    variable: "app_configuration_variable_discussion_forum_agg_responses",
    definition: "app_configuration_variable_definition_discussion_forum_agg_responses",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_instructions",
  },
];

export const DA_QUIZ_VARIABLES = [
  {
    variable: "app_configuration_variable_student_quiz_data",
    definition: "app_configuration_variable_definition_student_quiz_data",
  },
];

export const DA_AGG_QUIZ_VARIABLES = [
  {
    variable: "app_configuration_variable_aggregated_quiz_feedback_data",
    definition: "app_configuration_variable_definition_aggregated_quiz_feedback_data",
  },
  {
    variable: "app_configuration_variable_aggregated_quiz_feedback_instructions",
    definition: "app_configuration_variable_definition_aggregated_quiz_feedback_instructions",
  },
];

// LEA 1 to 1
export const LA_POST_VARIABLES = [
  {
    variable: "app_configuration_variable_discussion_topic",
    definition: "app_configuration_variable_definition_discussion_topic",
  },
  {
    variable: "app_configuration_variable_discussion_body",
    definition: "app_configuration_variable_definition_discussion_body",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_discussion_instructions",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_discussion_knowledge_base",
  },
  {
    variable: "app_configuration_variable_chat_history",
    definition: "app_configuration_variable_definition_chat_history",
  },
  {
    variable: "app_configuration_variable_student_latest_response",
    definition: "app_configuration_variable_definition_student_latest_response",
  },
];

// LEA Team Discussion (Live Chat)
export const LA_TEAM_VARIABLES = [
  {
    variable: "app_configuration_variable_discussion_topic",
    definition: "app_configuration_variable_definition_discussion_topic",
  },
  {
    variable: "app_configuration_variable_discussion_body",
    definition: "app_configuration_variable_definition_discussion_body",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_discussion_instructions",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_discussion_knowledge_base",
  },
  {
    variable: "app_configuration_variable_chat_history",
    definition: "app_configuration_variable_definition_chat_history",
  },
  {
    variable: "app_configuration_variable_discussion_student_query",
    definition: "app_configuration_variable_definition_discussion_student_response",
  },
];

// LEA Speech
export const LA_SPEECH_VARIABLES = [
  {
    variable: "app_configuration_variable_discussion_topic",
    definition: "app_configuration_variable_definition_discussion_topic",
  },
  {
    variable: "app_configuration_variable_discussion_body",
    definition: "app_configuration_variable_definition_discussion_body",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_discussion_instructions",
  },
  {
    variable: "app_configuration_variable_knowledge_base",
    definition: "app_configuration_variable_definition_discussion_knowledge_base",
  },
  {
    variable: "app_configuration_variable_chat_history",
    definition: "app_configuration_variable_definition_chat_history",
  },
];

// LEA Text Query
export const LA_TEXT_QUERY_VARIABLES = [
  {
    variable: "app_configuration_variable_text_query_student_selection",
    definition: "app_configuration_variable_definition_text_query_student_selection",
  },
  {
    variable: "app_configuration_variable_text_query_component_details",
    definition: "app_configuration_variable_definition_text_query_component_details",
  },
  {
    variable: "app_configuration_variable_text_query_question_details",
    definition: "app_configuration_variable_definition_text_query_question_details",
  },
  {
    variable: "app_configuration_variable_text_query_feedback_details",
    definition: "app_configuration_variable_definition_text_query_feedback_details",
  },
  {
    variable: "app_configuration_variable_text_query_student_latest_message",
    definition: "app_configuration_variable_definition_text_query_student_latest_message",
  },
  {
    variable: "app_configuration_variable_text_query_current_chat_history",
    definition: "app_configuration_variable_definition_text_query_current_chat_history",
  },
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_text_query_subject",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_text_query_level",
  },
  {
    variable: "app_configuration_variable_text_query_lesson_objective",
    definition: "app_configuration_variable_definition_text_query_lesson_objective",
  },
  {
    variable: "app_configuration_variable_activity_title",
    definition: "app_configuration_variable_definition_text_query_activity_title",
  },
  {
    variable: "app_configuration_variable_text_query_activity_context",
    definition: "app_configuration_variable_definition_text_query_activity_context",
  },
  {
    variable: "app_configuration_variable_text_query_activity_chat_history",
    definition: "app_configuration_variable_definition_text_query_activity_chat_history",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_text_query_instructions",
  },
];

export const AFA_VARIABLES = [
  {
    variable: "app_configuration_variable_subject",
    definition: "app_configuration_variable_definition_subject_question",
  },
  {
    variable: "app_configuration_variable_level",
    definition: "app_configuration_variable_definition_level_question",
  },
  {
    variable: "app_configuration_variable_question",
    definition: "app_configuration_variable_definition_question",
  },
  {
    variable: "app_configuration_variable_student_response",
    definition: "app_configuration_variable_definition_student_response",
  },
  {
    variable: "app_configuration_variable_modal_answer",
    definition: "app_configuration_variable_definition_modal_answer",
  },
  {
    variable: "app_configuration_variable_instructions",
    definition: "app_configuration_variable_definition_afa_instructions",
  },
  {
    variable: "app_configuration_variable_error_types",
    definition: "app_configuration_variable_definition_error_types",
  },
  {
    variable: "app_configuration_variable_rubrics",
    definition: "app_configuration_variable_definition_rubrics",
  },
];

export const PROMPT_CHARACTER_LIMIT = 30000;

export const SHORT_ANSWER_ERROR_CODE = {
  EXCEED_REQUEST_TOKENS: "exceed_request_tokens",
  CONTENT_FILTER: "content_filter",
};

export const APPEARANCE_SETTINGS_VIEW = { STANDARD: "standard", WIDE: "wide" };

export const APP_FEATURE_CATEGORY_VARIABLES_MAP = {
  [INTEGRATOR_APP_FEATURE_CATEGORY.SA]: SHORT_ANSWER_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE]: AA_MODULE_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY]: AA_ACTIVITY_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT]: AA_COMPONENT_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE]: AA_RE_COMPONENT_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_RUBRIC]: AA_RUBRIC_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE]: AA_IMAGE_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_GAMIFICATION]: AA_GAMIFICATION_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_POST]: DA_FORUM_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_FRQ]: DA_FRQ_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_QUIZ]: DA_QUIZ_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_QUIZ]: DA_AGG_QUIZ_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_POST]: LA_POST_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEAM]: LA_TEAM_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_SPEECH]: LA_SPEECH_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEXT_QUERY]: LA_TEXT_QUERY_VARIABLES,
  [INTEGRATOR_APP_FEATURE_CATEGORY.AFA]: AFA_VARIABLES,
};

export const APP_FEATURE_TYPE_VARIABLES_MAP = {
  [SA_APP_FEATURE_TYPE.SA_ANSWER]: SHORT_ANSWER_VARIABLES,
  [SA_APP_FEATURE_TYPE.SA_RUBRIC]: SHORT_ANSWER_RUBRIC_VARIABLES,
  [ACP_HTML5_APP_FEATURE_TYPE.ACP_HTML5_ANTHROPIC]: AA_HTML5_GENERATION_VARIABLES,
  [ACP_HTML5_APP_FEATURE_TYPE.ACP_HTML5_RE_ANTHROPIC]: AA_HTML5_ITERATION_VARIABLES,
};

export const ACP_REQUEST_STATUS = reflectKeys([
  "PENDING",
  "PROCESSING",
  "SUCCESS",
  "ERROR",
  "NO_RESULT",
  "FILE_VALIDATION_FAILED",
]);

export const ACP_HTML5_TYPE = reflectKeys(["DRAFT", "MO"]);
